import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../lib/auth';
import { ResonanceMark } from '../../components/ui/ResonanceMark';
import { PublicAppearanceSelector } from '../../components/ui/PublicAppearanceSelector';
import styles from './Public.module.css';

/**
 * Lands here after Google redirects back through the server, which appends a
 * one-time `?code=`. Exchanging it yields the token pair and bootstrap.
 *
 * The server's callback URL is currently a single server-wide value pointed at
 * the iOS scheme — see docs/SERVER_GAPS.md for what has to change server-side
 * before this route receives traffic.
 */
export function AuthCallbackPage() {
  const { completeOAuth } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const exchanged = useRef(false);

  useEffect(() => {
    if (exchanged.current) return;
    exchanged.current = true;

    const code = new URLSearchParams(window.location.search).get('code');
    if (!code) {
      setError('The sign-in link is missing its code.');
      return;
    }

    completeOAuth(code)
      .then(() => navigate({ to: '/app', replace: true }))
      .catch((caught: unknown) => {
        setError(caught instanceof Error ? caught.message : 'Sign-in could not be completed.');
      });
  }, [completeOAuth, navigate]);

  return (
    <div className={styles.screen}>
      <div className="appGradient" />
      <div className={styles.appearanceSelector}>
        <PublicAppearanceSelector />
      </div>
      <div className={styles.card}>
        <div className={styles.brand}>
          <ResonanceMark size={26} className={styles.mark} />
          Resonance
        </div>
        {error ? (
          <>
            <p className={styles.error}>{error}</p>
            <button type="button" className={styles.secondary} onClick={() => void navigate({ to: '/sign-in' })}>
              Back to sign in
            </button>
          </>
        ) : (
          <p className={styles.subtitle}>Signing you in…</p>
        )}
      </div>
    </div>
  );
}
